import React, {useCallback, useEffect, useMemo, useState} from 'react';
import clsx from 'clsx';
import 'core-css/ui/iconDefaults.css';
import {useResizeDetector} from 'react-resize-detector';
import {Fade, Skeleton, type SkeletonProps} from '@mui/material';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {preventDefault} from '@netx/core-next/utils/browser';
import {
	GENERIC_IMAGE_CLASS_NAME,
	genericImageClasses,
	type GenericImageClasses,
	getGenericImageUtilityClass,
} from './genericImageClasses';
import {GenericImageWatermark} from './GenericImageWatermark';
import {useGenericImageWatermark} from './useGenericImageWatermark';
import {type Dims, getOrientation, isDefaultIcon, Orientation} from './utils';

export type GenericImageElement = HTMLDivElement;

export type GenericImageRef = React.ForwardedRef<GenericImageElement>;

export type GenericImageOwnerState = WithClasses<
	GenericImageClasses,
	{
		defaultIcon?: boolean;
		portrait?: boolean;
		landscape?: boolean;
		watermarkSrc?: string | null;
	}
>;

const useUtilityClasses = (ownerState: GenericImageOwnerState) => {
	const {classes, defaultIcon, landscape, portrait} = ownerState;
	const slots = {
		root: [
			'root',
			landscape && 'landscape',
			portrait && 'portrait',
			defaultIcon && 'defaultIcon',
		],
		customIcon: ['customIcon'],
		image: ['image'],
		imageWrapper: ['imageWrapper'],
		skeleton: ['skeleton'],
		watermark: ['watermark'],
	};
	return composeClasses(slots, getGenericImageUtilityClass, classes);
};

const GenericImageRoot = styled('div', {
	name: GENERIC_IMAGE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	alignItems: 'center',
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: 0,
	overflow: 'hidden',
	position: 'relative',
	userSelect: 'none',
	[`&.${genericImageClasses.landscape}`]: {
		[`& .${genericImageClasses.image}`]: {
			maxWidth: '100%',
		},
	},
	[`&.${genericImageClasses.portrait}`]: {
		[`& .${genericImageClasses.image}`]: {
			maxHeight: '100%',
			maxWidth: '100%',
		},
	},
	[`&.${genericImageClasses.defaultIcon}`]: {
		[`& .${genericImageClasses.imageWrapper}`]: {
			height: '100%',
			width: '100%',
		},
	},
}));

const GenericImageWrapper = styled('div', {
	name: GENERIC_IMAGE_CLASS_NAME,
	slot: 'ImageWrapper',
})(({theme}) => ({
	position: 'relative',
	display: 'inline-flex',
	// alignItems: 'center',
	// justifyContent: 'center',
	maxHeight: '100%',
	maxWidth: '100%',
	// safari fix
	minHeight: 0,
	minWidth: 0,
}));

const GenericImageImg = styled('img', {
	name: GENERIC_IMAGE_CLASS_NAME,
	slot: 'Image',
})(({theme}) => ({
	width: 'auto',
	height: 'auto',
	margin: 'auto',
	maxHeight: '100%',
	maxWidth: '100%',
	// height: '100%',
	// width: '100%',
	objectFit: 'contain',
	WebkitUserSelect: 'none',
	WebkitTouchCallout: 'none',
}));

const GenericImageSkeleton = styled(Skeleton, {
	name: GENERIC_IMAGE_CLASS_NAME,
	slot: 'Skeleton',
})(({theme}) => ({
	height: '100%',
	width: '100%',
}));

type GenericImageCustomIconProps = React.DetailedHTMLProps<
	React.HTMLAttributes<HTMLDivElement>,
	HTMLDivElement
>;

const GenericImageCustomIconRoot = styled('div', {
	name: GENERIC_IMAGE_CLASS_NAME,
	slot: 'CustomIcon',
})(({theme}) => ({
	alignContent: 'center',
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	justifyContent: 'center',
	position: 'relative',
	height: '100%',
	width: '100%',
	containerType: 'inline-size', // Enable container queries
	'& .thumbnail': {
		height: '100%',
		width: '100%',
		flex: '1 1 auto',
		fontSize: 12,
		// Theme wide styles are messing with this in custom icons
		margin: 'auto !important',
		// maxHeight: 500,
		// maxWidth: 500,
	},
}));

export const GenericImageCustomIcon = (props: GenericImageCustomIconProps) => {
	const {width = 0, ref} = useResizeDetector({
		handleWidth: true,
	});
	return (
		// piggy backing off of our "wonderful" custom icon styles
		<GenericImageCustomIconRoot ref={ref} {...props}>
			<div
				className={clsx(
					'thumbnail',
					width < 40
						? 'fixed-size-tiny'
						: width < 51
							? 'fixed-size-small'
							: null,
				)}
			/>
		</GenericImageCustomIconRoot>
	);
};

export interface GenericImageProps
	extends
		Omit<
			React.DetailedHTMLProps<
				React.HTMLAttributes<GenericImageElement>,
				GenericImageElement
			>,
			'alt' | 'onLoad' | 'onResize'
		>,
		WithClasses<
			GenericImageClasses,
			React.PropsWithChildren<{
				alt: string;
				disableFade?: boolean;
				disableWatermark?: boolean;
				imageDims?: Dims;
				loading?: boolean;
				onLoad?: (img: HTMLImageElement, width: number, height: number) => void;
				queryParams?: Record<string, string | number>;
				src?: string;
				wrapperChildren?: React.ReactNode;
				SkeletonProps?: Partial<SkeletonProps>;
			}>
		> {}

export const GenericImagePrimitive = (
	{
		classes: _classes,
		className,
		children,
		disableWatermark,
		alt,
		imageDims: _imageDims,
		disableFade,
		loading,
		queryParams,
		src,
		wrapperChildren,
		SkeletonProps,
		onLoad,
		...props
	}: GenericImageProps,
	ref: GenericImageRef,
) => {
	const [error, setError] = useState(false);
	// const [containerDims, setContainerDims] = useState({width: 0, height: 0});
	const [imageDims, setImageDims] = useState(
		_imageDims || {width: 0, height: 0},
	);
	const [orientation, setOrientation] = useState<Orientation>(
		Orientation.PORTRAIT,
	);

	useEffect(() => {
		setError(false);
	}, [src]);

	// Test if we are dealing with a default icon from the server
	const defaultIcon = useMemo(() => {
		if (error || !src) return ['custom-icon-image', 'image'];
		return isDefaultIcon(src);
	}, [src, error]);

	const {watermarkSrc} = useGenericImageWatermark({queryParams});

	const ownerState: GenericImageOwnerState = {
		classes: _classes,
		className,
		defaultIcon: defaultIcon != null,
		landscape: orientation === Orientation.LANDSCAPE,
		portrait: orientation === Orientation.PORTRAIT,
		watermarkSrc: disableWatermark ? undefined : watermarkSrc,
	};

	const classes = useUtilityClasses(ownerState);

	// On container resize, set the appropriate proxy for the current container size
	// const handleResize = useCallback(
	// 	(width = 0, height = 0) => {
	// 		if (width === 0 || height === 0) return;
	// 		setContainerDims({width, height});
	// 		onResize?.(width, height);
	// 	},
	// 	[onResize],
	// );

	const handleLoadError = useConstCallback(() => setError(true));

	const handleLoaded = useConstCallback(
		(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
			const {naturalHeight, naturalWidth} = event.target as HTMLImageElement;
			if (!_imageDims)
				setImageDims({
					width: (event.target as HTMLImageElement).naturalWidth,
					height: (event.target as HTMLImageElement).naturalHeight,
				});

			setOrientation(
				getOrientation({
					width: naturalWidth,
					height: naturalHeight,
				}),
			);

			onLoad?.(event.target as HTMLImageElement, naturalWidth, naturalHeight);
		},
	);

	return (
		<GenericImageRoot
			data-testid="generic-image"
			{...props}
			className={clsx(classes.root, className)}
			onContextMenu={preventDefault}
			ref={ref}
			ownerState={ownerState}
		>
			{loading ? (
				<GenericImageSkeleton
					animation={false}
					variant="rectangular"
					{...SkeletonProps}
				/>
			) : src || defaultIcon ? (
				<Fade in timeout={disableFade ? 0 : 300}>
					<GenericImageWrapper className={classes.imageWrapper}>
						{defaultIcon ? (
							<GenericImageCustomIcon
								className={clsx(
									classes.customIcon,
									`custom-icon-${defaultIcon[1]}`,
								)}
							/>
						) : (
							<>
								<GenericImageImg
									draggable="false"
									alt={alt}
									className={classes.image}
									onLoad={handleLoaded}
									onError={handleLoadError}
									{...(imageDims.width && imageDims.height ? imageDims : {})}
									onContextMenu={preventDefault}
									src={src}
									loading="lazy"
								/>
								{!disableWatermark && (
									<GenericImageWatermark
										queryParams={queryParams}
										className={classes.watermark}
									/>
								)}
								{wrapperChildren}
							</>
						)}
					</GenericImageWrapper>
				</Fade>
			) : null}
			{children}
		</GenericImageRoot>
	);
};

export const GenericImage = React.forwardRef<
	GenericImageElement,
	GenericImageProps
>(GenericImagePrimitive);

export default GenericImage;
